/**
 * Oversubscription Calculator Component
 *
 * Displays detailed network oversubscription analysis including blocking
 * factors, bandwidth calculations, and network health metrics.
 */

import { useState } from 'react';
import { Section } from '../layout/Section';
import { useStore } from '../../store/store';

export function OversubscriptionCalculator() {
  const { configuration } = useStore();
  const { network, scaling } = configuration;
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate East-West oversubscription (using blocking factor)
  const calculateEWOversubscription = () => {
    const { eastWest } = network;
    const { blockingFactor, portsPerNode, speedGbps } = eastWest;

    const nodes = Math.ceil(scaling.totalGPUs / scaling.gpusPerNode);
    const totalNodeLinks = nodes * portsPerNode;

    // Calculate port allocation using blocking factor
    const leafSwitchPorts = eastWest.leafSwitch.ports;
    const ibBlockingFactor = blockingFactor || 1.0;
    const downlinksPerLeaf = Math.floor(leafSwitchPorts * ibBlockingFactor / (ibBlockingFactor + 1));
    const uplinksPerLeaf = leafSwitchPorts - downlinksPerLeaf;

    // Calculate leaf switches needed (with diversity constraint)
    const minLeafsForCapacity = Math.ceil(totalNodeLinks / downlinksPerLeaf);
    const minLeafsForDiversity = portsPerNode;
    const leafCount = Math.max(minLeafsForCapacity, minLeafsForDiversity);

    // Total bandwidth calculations
    const totalDownlinkBandwidthGbps = leafCount * downlinksPerLeaf * speedGbps;
    const totalUplinkBandwidthGbps = leafCount * uplinksPerLeaf * speedGbps;

    // Oversubscription ratio
    const oversubscriptionRatio =
      totalUplinkBandwidthGbps > 0
        ? totalDownlinkBandwidthGbps / totalUplinkBandwidthGbps
        : 0;

    // Calculated blocking factor for display (inverse of oversubscription)
    const calculatedBlockingFactor = oversubscriptionRatio > 1 ? oversubscriptionRatio : 1;

    // Per-node bandwidth
    const perNodeDownlinkGbps = portsPerNode * speedGbps;
    const effectivePerNodeUplinkGbps = perNodeDownlinkGbps / oversubscriptionRatio;

    return {
      oversubscriptionRatio,
      blockingFactor: calculatedBlockingFactor,
      isNonBlocking: oversubscriptionRatio <= 1.0,
      totalDownlinkBandwidthTbps: totalDownlinkBandwidthGbps / 1000,
      totalUplinkBandwidthTbps: totalUplinkBandwidthGbps / 1000,
      perNodeDownlinkGbps,
      effectivePerNodeUplinkGbps,
      leafCount,
      downlinksPerLeaf,
      uplinksPerLeaf,
    };
  };

  // Calculate North-South oversubscription
  const calculateNSOversubscription = () => {
    const { northSouth } = network;
    const { downlinkRatio, portsPerNode, speedGbps } = northSouth;

    const nodes = Math.ceil(scaling.totalGPUs / scaling.gpusPerNode);
    const totalNodeLinks = nodes * portsPerNode;

    const leafSwitchPorts = northSouth.leafSwitch.ports;
    const downlinksPerLeaf = Math.floor(leafSwitchPorts * downlinkRatio);
    const uplinksPerLeaf = leafSwitchPorts - downlinksPerLeaf;

    const leafCount = Math.ceil(totalNodeLinks / downlinksPerLeaf);

    const totalDownlinkBandwidthGbps = leafCount * downlinksPerLeaf * speedGbps;
    const totalUplinkBandwidthGbps = leafCount * uplinksPerLeaf * speedGbps;

    const oversubscriptionRatio =
      totalUplinkBandwidthGbps > 0
        ? totalDownlinkBandwidthGbps / totalUplinkBandwidthGbps
        : 0;

    const blockingFactor = oversubscriptionRatio > 1 ? oversubscriptionRatio : 1;

    return {
      oversubscriptionRatio,
      blockingFactor,
      isNonBlocking: oversubscriptionRatio <= 1.0,
      totalDownlinkBandwidthTbps: totalDownlinkBandwidthGbps / 1000,
      totalUplinkBandwidthTbps: totalUplinkBandwidthGbps / 1000,
    };
  };

  const ewMetrics = calculateEWOversubscription();
  const nsMetrics = calculateNSOversubscription();

  // Determine health status
  const getHealthStatus = (ratio: number) => {
    if (ratio <= 1.0) return { color: 'text-green-700', bg: 'bg-green-50', status: 'Excellent' };
    if (ratio <= 2.0) return { color: 'text-yellow-700', bg: 'bg-yellow-50', status: 'Good' };
    if (ratio <= 4.0) return { color: 'text-orange-700', bg: 'bg-orange-50', status: 'Fair' };
    return { color: 'text-red-700', bg: 'bg-red-50', status: 'Poor' };
  };

  const ewHealth = getHealthStatus(ewMetrics.oversubscriptionRatio);
  const nsHealth = getHealthStatus(nsMetrics.oversubscriptionRatio);

  return (
    <Section
      title="Oversubscription Analysis"
      collapsible
      isExpanded={isExpanded}
      onToggle={() => setIsExpanded(!isExpanded)}
    >
      <div className="space-y-4">
        {/* East-West Network */}
        <div className={`p-3 ${ewHealth.bg} border border-gray-300 rounded`}>
          <div className="flex justify-between items-center mb-2">
            <span className="font-semibold text-xs">East-West (IB) Network</span>
            <span className={`text-xs font-bold ${ewHealth.color}`}>{ewHealth.status}</span>
          </div>

          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <div className="text-gray-600">Oversubscription</div>
              <div className="font-semibold">
                {ewMetrics.oversubscriptionRatio.toFixed(2)}:1
              </div>
            </div>

            <div>
              <div className="text-gray-600">Blocking Factor</div>
              <div className="font-semibold">
                {ewMetrics.isNonBlocking ? 'Non-blocking' : `${ewMetrics.blockingFactor.toFixed(1)}:1`}
              </div>
            </div>

            <div>
              <div className="text-gray-600">Total Downlink BW</div>
              <div className="font-semibold">{ewMetrics.totalDownlinkBandwidthTbps.toFixed(1)} Tbps</div>
            </div>

            <div>
              <div className="text-gray-600">Total Uplink BW</div>
              <div className="font-semibold">{ewMetrics.totalUplinkBandwidthTbps.toFixed(1)} Tbps</div>
            </div>

            <div>
              <div className="text-gray-600">Per-Node Downlink</div>
              <div className="font-semibold">{ewMetrics.perNodeDownlinkGbps} Gbps</div>
            </div>

            <div>
              <div className="text-gray-600">Effective Per-Node Uplink</div>
              <div className="font-semibold">{ewMetrics.effectivePerNodeUplinkGbps.toFixed(0)} Gbps</div>
            </div>
          </div>

          <div className="mt-2 text-[10px] text-gray-600">
            {ewMetrics.leafCount} leaf switches ({ewMetrics.downlinksPerLeaf} down / {ewMetrics.uplinksPerLeaf} up ports each)
          </div>
        </div>

        {/* North-South Network */}
        <div className={`p-3 ${nsHealth.bg} border border-gray-300 rounded`}>
          <div className="flex justify-between items-center mb-2">
            <span className="font-semibold text-xs">North-South (Ethernet) Network</span>
            <span className={`text-xs font-bold ${nsHealth.color}`}>{nsHealth.status}</span>
          </div>

          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <div className="text-gray-600">Oversubscription</div>
              <div className="font-semibold">
                {nsMetrics.oversubscriptionRatio.toFixed(2)}:1
              </div>
            </div>

            <div>
              <div className="text-gray-600">Blocking Factor</div>
              <div className="font-semibold">
                {nsMetrics.isNonBlocking ? 'Non-blocking' : `${nsMetrics.blockingFactor.toFixed(1)}:1`}
              </div>
            </div>

            <div>
              <div className="text-gray-600">Total Downlink BW</div>
              <div className="font-semibold">{nsMetrics.totalDownlinkBandwidthTbps.toFixed(1)} Tbps</div>
            </div>

            <div>
              <div className="text-gray-600">Total Uplink BW</div>
              <div className="font-semibold">{nsMetrics.totalUplinkBandwidthTbps.toFixed(1)} Tbps</div>
            </div>
          </div>
        </div>

        {/* Explanation */}
        <div className="p-2 bg-blue-50 border border-blue-200 rounded text-[10px]">
          <div className="font-semibold text-blue-900 mb-1">Understanding Oversubscription</div>
          <ul className="text-gray-700 space-y-1">
            <li><strong>1:1 (Non-blocking):</strong> Full bisection bandwidth, no contention</li>
            <li><strong>2:1:</strong> Standard for most clusters, good cost/performance balance</li>
            <li><strong>3-4:1:</strong> Acceptable for workloads with locality</li>
            <li><strong>&gt;4:1:</strong> May impact performance for all-to-all traffic</li>
          </ul>
        </div>
      </div>
    </Section>
  );
}
